import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  CircularProgress,
  Stack,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import VSNTypography from "../../shared/VSNTypography";
import VSNButton from "../../shared/VSNButton";
import { toggleModelShow } from "../../shared/CustomFunc";
import VSNModel from "../../shared/VSNModel";
import documentsContainer from "../../container/documents.container";
import VSNSelect from "../../shared/VSNSelect";
import VSNTextField from "../../shared/VSNTextField";
import VSNLabel from "../../shared/VSNLabel";
import UploadDocumentModal from "./UploadDocument";
import VSNConfirmBox from "../../shared/VSNConfirmBox";

const useStyles = makeStyles((theme) => ({
  inviteAdminModel: {
    "&.MuiModal-root": {
      "& .MuiPaper-root": {
        maxWidth: "458px",
      },
    },
  },
  FilterOrganizationInput: {
    minWidth: "135px",
    backgroundColor: "white",
    [theme.breakpoints.down("375")]: {
      minWidth: "119px",
    },
    "& .MuiSelect-select": {
      padding: "11px 32px 11px 12px",
      fontSize: 14,
    },
    "& .MuiSvgIcon-root": {
      position: "absolute",
      right: 7,
      pointerEvents: "none",
    },
  },

  modalHead: {
    "&.MuiStack-root": {
      padding: "20px 0px",
      borderBottom: "1px solid #EAEDF0",
      "& h4": {
        fontWeight: 600,
        fontSize: 24,
        padding: 0,
        lineHeight: "36px",
        color: "var(--blackGray)",
        [theme.breakpoints.down("md")]: {
          marginBottom: 12,
          fontSize: 22,
          lineHeight: "30px",
        },
        [theme.breakpoints.down("sm")]: {
          fontSize: 18,
        },
      },
      "& .MuiIconButton-root": {
        backgroundColor: "var(--primaryLighter)",
        height: 25,
        width: 25,
        "& svg": {
          margin: 0,
          marginBottom: 0,
          color: "var(--primary)",
        },
        "&:hover": {
          backgroundColor: "var(--primaryGradientLighter)",
        },
      },
    },
  },
  question: {
    "&.MuiTypography-root": {
      fontWeight: 700,
      lineHeight: "23.44px",
      fontSize: "24px",
      color: "#2D2F39",
      position: "relative",
      display: "inline-block",
      "& .MuiFormLabel-root": {
        position: "absolute",
        left: "100%",
      },
    },
  },
  description: {
    "&.MuiTypography-root": {
      color: "var(--gray)",
      fontWeight: 900,
      fontSize: "14px",
    },
  },
  button_groups: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "#F6F6FD",
    padding: "20px 25px",
    gap: 10,
    marginLeft: "-25px",
    marginRight: "-25px",
    position: "sticky",
    bottom: 0,
    zIndex: 99,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: "15px",
      gap: 10,
      marginLeft: "-15px",
      marginRight: "-15px",
    },
  },
  button: {
    "&.MuiButtonBase-root": {
      color: "var(--white)",
      padding: "13px 25px",
      borderRadius: 10,
      minWidth: 176,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
      "& span.icon": {
        marginLeft: 8,
        display: "inline-block",
      },
      "& .content": {
        fontWeight: 500,
        fontSize: 16,
        lineHeight: "16px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      "& .loader": {
        "& .MuiCircularProgress-root": {
          height: "16px !important",
          width: "16px !important",
        },
      },
      "&.link": {
        background: "var(--white)",
        border: "1px solid var(--Tertiary)",
        color: "var(--Tertiary)",
        "& .linkIcon": {
          height: "16px",
          width: "16px",
          display: "inline-block",
          marginRight: 10,
        },
      },
    },
  },
  documentTable: {
    width: "100%",
    margin: "auto",
    px: 2,
    border: `1px solid #EAEAEC`,
    borderRadius: 16,
    "& .MuiTableHead-root": {
      "& .MuiTableCell-root": {
        "&:first-child": {
          width: "40%",
        },
      },
    },
    "& .MuiTableBody-root": {
      "& .MuiTableRow-root": {
        "&:last-child": {
          "& .MuiTableCell-root": {
            border: 0,
          },
        },
      },
    },
  },
  filterSection: {
    [theme.breakpoints.down("lg")]: {
      "& .MuiButtonBase-root": {
        padding: "8px 24px",
        minWidth: "unset",
        "& .content": {
          fontSize: 16,
        },
      },
      "& .MuiFormControl-root": {
        "& .MuiSelect-select": {
          paddingTop: 8,
          paddingBottom: 8,
        },
      },
    },
  },
}));

const Document = () => {
  const {
    documentResult,
    documentLoader,
    handleDocumentDownload,
    dispatch,
    modelState,
    handleDocumentDelete,
    uploadedBy,
    setUploadedBy,
    handleDocumentUpdate,
    updatedName,
    setUpdatedName,
    handleUpdateSubmit,
    onDeleteClick,
    showDialog,
    downloadLoading,
  } = documentsContainer();
  const classes = useStyles();

  return (
    <Box>
      <Stack
        direction={{ md: "row", xs: "column" }}
        justifyContent="space-between"
        alignItems={{ md: "center", xs: "flex-start" }}
        mb={2}
        gap={1}
      >
        <VSNTypography variant="h6" sx={{ fontWeight: 700, fontSize: "24px" }}>
          Documents
        </VSNTypography>

        <Stack
          direction="row"
          gap={{ xs: 1, sm: 2 }}
          alignItems="center"
          className={classes.filterSection}
          width={{ xs: "100%", md: "auto" }}
        >
          <VSNSelect
            onChange={(event) => setUploadedBy(event.target.value)}
            value={uploadedBy}
            options={[
              { key: "All", value: "all" },
              { key: "Organization", value: "organization" },
              { key: "Self", value: "self" },
            ]}
            className={classes.FilterOrganizationInput}
          />

          <VSNButton
            handleClick={() => dispatch(toggleModelShow("uploadDocumnet"))}
            // className={classes.btn}
            startIcon={<FileUploadOutlinedIcon />}
            sx={{ ml: "auto" }}
          >
            <span>Upload</span>
          </VSNButton>
        </Stack>
      </Stack>
      <TableContainer className={classes.documentTable}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Document Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Uploaded by</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Size</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {documentLoader ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center" }}>
                  <CircularProgress size={24} color="inherit" />
                </TableCell>
              </TableRow>
            ) : documentResult?.length > 0 ? (
              documentResult?.map((doc, index) => (
                <TableRow key={index}>
                  <TableCell>{doc?.name}</TableCell>
                  <TableCell>
                    <VSNTypography>{doc?.uploaded_by}</VSNTypography>
                  </TableCell>
                  <TableCell>
                    <VSNTypography>{doc?.size}</VSNTypography>
                  </TableCell>
                  <TableCell>
                    <VSNTypography>{doc?.uploaded_at}</VSNTypography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" gap={2}>
                      <IconButton
                        sx={{ borderRadius: 1.5, backgroundColor: "#F2E7F5" }}
                        onClick={() => handleDocumentUpdate(doc)}
                      >
                        <BorderColorOutlinedIcon style={{ color: "#8B3F9E" }} />
                      </IconButton>

                      <IconButton
                        sx={{ borderRadius: 1.5, backgroundColor: "#F2FBF5" }}
                        onClick={() => handleDocumentDownload(doc?.id)}
                      >
                        {downloadLoading[doc?.id] ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          <DownloadOutlinedIcon style={{ color: "#34A259" }} />
                        )}
                      </IconButton>

                      <IconButton
                        sx={{ borderRadius: 1.5, backgroundColor: "#FEF2F2" }}
                        onClick={() => onDeleteClick(doc?.id)}
                      >
                        <DeleteOutlineIcon style={{ color: "#F54346" }} />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  sx={{ textAlign: "center", color: "gray" }}
                >
                  No Document Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {modelState?.uploadDocumnet && (
        <VSNModel
          open={modelState?.uploadDocumnet}
          onClose={() => {
            dispatch(
              toggleModelShow("uploadDocumnet")
              // dispatch(clearFormData(formPath.parent))
            );
          }}
          disableEnforceFocus
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          mobileDrawer
          className={classes.inviteAdminModel}
        >
          <UploadDocumentModal
            // onSuccess={onSuccess}
            open={modelState?.uploadDocumnet}
            pageName="document"
          />
        </VSNModel>
      )}
      {modelState?.updateDocumentName && (
        <VSNModel
          open={modelState?.updateDocumentName}
          onClose={() => {
            dispatch(
              toggleModelShow("updateDocumentName")
              // dispatch(clearFormData(formPath.parent))
            );
          }}
          disableEnforceFocus
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          mobileDrawer
          className={classes.inviteAdminModel}
        >
          <Box>
            <Stack position="relative">
              <Stack direction="column" className={classes.modalHead}>
                <VSNTypography className={classes.question}>
                  Rename File
                </VSNTypography>
              </Stack>
              <Box my={1.5} width="100%">
                <VSNTypography className={classes.description}>
                  Enter File Name
                </VSNTypography>
                <VSNTextField
                  fullWidth
                  margin="normal"
                  label="Enter File Name"
                  variant="standard"
                  value={updatedName}
                  onChange={(e) => setUpdatedName(e.target.value)}
                />
              </Box>
            </Stack>

            <Box className={classes.button_groups}>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <VSNButton
                  handleClick={() =>
                    dispatch(toggleModelShow("updateDocumentName"))
                  }
                  variant="outlined"
                >
                  Cancel
                </VSNButton>
                <VSNButton
                  handleClick={handleUpdateSubmit}
                  disabled={!updatedName.trim()}
                  isLoading={documentLoader}
                >
                  Save
                </VSNButton>
              </Stack>
            </Box>
          </Box>
        </VSNModel>
      )}
      {showDialog && (
        <VSNConfirmBox
          isOpen={showDialog}
          title="Are You Sure?"
          msg="Are you sure you want to delete ?"
          handleOk={handleDocumentDelete}
          handleCancel={() => onDeleteClick(null)}
          okBtnName="Delete"
          cancelBtnName="Cancel"
          className={classes.conformModel}
          isLoading={documentLoader}
        />
      )}
    </Box>
  );
};

export default Document;
